import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import Calendar from './components/Calendar';
import Photo, { VideoEntry } from './components/Photo';
import BlogEntry from './components/BlogEntry';

// Extract the calendar date (YYYY-MM-DD) from an entry date like "2024-10-15-2-v"
function getCalendarDate(dateStr) {
  // Match YYYY-MM-DD at the start
  const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : dateStr;
}

function App() {
  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/data.json`)
      .then((res) => res.json())
      .then((data) => {
        setEntries(data);
        if (data.length > 0) {
          const random = data[Math.floor(Math.random() * data.length)];
          setSelectedDate(random.date);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const entryMap = useMemo(() => {
    const map = {};
    entries.forEach((e) => { map[e.date] = e; });
    return map;
  }, [entries]);

  // Build set of calendar dates that have entries (for highlighting days)
  const calendarDates = useMemo(() => {
    const set = new Set();
    entries.forEach((e) => set.add(getCalendarDate(e.date)));
    return set;
  }, [entries]);

  // Build map: calendar date -> array of entry dates on that day
  const dateToEntries = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      const cd = getCalendarDate(e.date);
      if (!map[cd]) map[cd] = [];
      map[cd].push(e.date);
    });
    return map;
  }, [entries]);

  const selectedEntry = selectedDate ? entryMap[selectedDate] : null;

  const currentIndex = selectedDate ? entries.findIndex((e) => e.date === selectedDate) : -1;
  const prevEntry = currentIndex > 0 ? entries[currentIndex - 1] : null;
  const nextEntry = currentIndex < entries.length - 1 ? entries[currentIndex + 1] : null;

  // When a calendar date is clicked, select the first entry on that day
  const handleCalendarDateSelect = (calendarDate) => {
    const entriesOnDay = dateToEntries[calendarDate];
    if (entriesOnDay && entriesOnDay.length > 0) {
      setSelectedDate(entriesOnDay[0]);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <h1 className="text-center mb-4">🐻🐼 First Year</h1>
      <div className="row content-row">
        <div className="col-md-8 content-col">
          {selectedEntry ? (
            <>
              {selectedEntry.type === 'video' ? (
                <VideoEntry
                  video={selectedEntry.video}
                  poster={selectedEntry.poster}
                />
              ) : (
                <Photo
                  image={selectedEntry.image}
                  alt={selectedEntry.title}
                  liveVideo={selectedEntry.liveVideo}
                />
              )}
              <BlogEntry
                title={selectedEntry.title}
                entry={selectedEntry.entry}
                date={getCalendarDate(selectedEntry.date)}
              />
              {(() => {
                const calDate = getCalendarDate(selectedEntry.date);
                const dayEntries = dateToEntries[calDate] || [];
                if (dayEntries.length <= 1) return null;
                const dayIndex = dayEntries.indexOf(selectedEntry.date);
                return (
                  <div className="day-entry-nav">
                    <button
                      className="day-entry-arrow"
                      onClick={() => setSelectedDate(dayEntries[dayIndex - 1])}
                      disabled={dayIndex <= 0}
                      aria-label="Previous entry on this day"
                    >
                      &lsaquo;
                    </button>
                    <div className="day-entry-dots">
                      {dayEntries.map((d, i) => (
                        <button
                          key={d}
                          className={`day-entry-dot${i === dayIndex ? ' active' : ''}`}
                          onClick={() => setSelectedDate(d)}
                          aria-label={`Entry ${i + 1} of ${dayEntries.length}`}
                        />
                      ))}
                    </div>
                    <button
                      className="day-entry-arrow"
                      onClick={() => setSelectedDate(dayEntries[dayIndex + 1])}
                      disabled={dayIndex >= dayEntries.length - 1}
                      aria-label="Next entry on this day"
                    >
                      &rsaquo;
                    </button>
                  </div>
                );
              })()}
              <div className="entry-nav">
                <button
                  className="entry-nav-btn"
                  onClick={() => setSelectedDate(prevEntry.date)}
                  disabled={!prevEntry}
                >
                  &larr; Previous
                </button>
                <button
                  className="entry-nav-btn"
                  onClick={() => setSelectedDate(nextEntry.date)}
                  disabled={!nextEntry}
                >
                  Next &rarr;
                </button>
              </div>
            </>
          ) : (
            <p className="text-muted">Select a date to see the entry.</p>
          )}
        </div>
        <div className="col-md-4 mb-4 calendar-col">
          <Calendar
            dates={[...calendarDates]}
            selectedDate={selectedDate ? getCalendarDate(selectedDate) : null}
            onDateSelect={handleCalendarDateSelect}
            dateToEntries={dateToEntries}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
